import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuerySample } from '@store/helpers';
import {
  ConversationDTO,
  MessageDTO,
  toChatMessage,
  toChatMessages,
  toDriverConversation,
} from '@component/DriverChat/adapters';
import { DRIVER_CHAT_MESSAGES_PAGE_SIZE } from '@component/DriverChat/constants';
import {
  ChatMessage,
  ChatThreadData,
  DriverConversation,
} from '@component/DriverChat/types';

export type ConversationFilter = 'ACTIVE' | 'TODAY';
export const CONVERSATION_FILTERS: ConversationFilter[] = ['ACTIVE', 'TODAY'];
const LIST_SIZE = 50;

type SendMessageArgs = {
  conversationId: number;
  text: string;
  clientMessageId: string;
};

type ReadArgs = { conversationId: number; lastReadMessageId: number };

type ListArgs = { filter: ConversationFilter };

type OlderArgs = { conversationId: number; beforeMessageId: number };

const toThreadData = (response: {
  messages?: MessageDTO[];
  hasMore?: boolean;
}): ChatThreadData => ({
  messages: toChatMessages(response?.messages),
  hasMore: !!response?.hasMore,
});

const BASE = '/twilio/driver-chat';
const BASE_SOCKET = '/websocket/driver-chat';

export const driverChatApi = createApi({
  reducerPath: 'DriverChat',
  tagTypes: ['Conversations', 'Messages', 'Unread'],
  baseQuery: baseQuerySample,

  endpoints: (builder) => ({
    getDriverConversations: builder.query<DriverConversation[], ListArgs>({
      query: ({ filter }) => ({
        url: `${BASE}/conversations`,
        params: { filter, page: 0, size: LIST_SIZE },
      }),
      transformResponse: (response: { content?: ConversationDTO[] }) =>
        (response?.content ?? []).map(toDriverConversation),
      providesTags: ['Conversations'],
    }),

    openDriverConversation: builder.mutation<DriverConversation, number>({
      query: (rideSegmentId) => ({
        url: `${BASE}/conversations`,
        method: 'POST',
        body: { rideSegmentId },
      }),
      transformResponse: (response: ConversationDTO) =>
        toDriverConversation(response),
      invalidatesTags: ['Conversations'],
    }),

    getDriverConversation: builder.query<DriverConversation, number>({
      query: (conversationId) => ({
        url: `${BASE}/conversations/${conversationId}`,
      }),
      transformResponse: (response: ConversationDTO) =>
        toDriverConversation(response),
      providesTags: (_result, _error, id) => [{ type: 'Conversations', id }],
    }),

    getDriverChatMessages: builder.query<ChatThreadData, number>({
      query: (conversationId) => ({
        url: `${BASE}/conversations/${conversationId}/messages`,
        params: { limit: DRIVER_CHAT_MESSAGES_PAGE_SIZE },
      }),
      transformResponse: toThreadData,
      providesTags: (_result, _error, conversationId) => [
        { type: 'Messages', id: conversationId },
      ],
    }),

    loadOlderDriverChatMessages: builder.mutation<ChatThreadData, OlderArgs>({
      query: ({ conversationId, beforeMessageId }) => ({
        url: `${BASE}/conversations/${conversationId}/messages`,
        params: { beforeMessageId, limit: DRIVER_CHAT_MESSAGES_PAGE_SIZE },
      }),
      transformResponse: toThreadData,
      async onQueryStarted({ conversationId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            driverChatApi.util.updateQueryData(
              'getDriverChatMessages',
              conversationId,
              (draft) => {
                const known = new Set(draft.messages.map((m) => m.id));
                draft.messages = [
                  ...data.messages.filter((m) => !known.has(m.id)),
                  ...draft.messages,
                ];
                draft.hasMore = data.hasMore;
              }
            )
          );
        } catch {
          // The button stays available; the next click retries.
        }
      },
    }),

    // Sent over REST (not STOMP) so every message is persisted and audited
    // server-side; clientMessageId makes retries idempotent (no second SMS).
    sendDriverChatMessage: builder.mutation<ChatMessage, SendMessageArgs>({
      query: ({ conversationId, text, clientMessageId }) => ({
        url: `${BASE}/conversations/${conversationId}/messages`,
        method: 'POST',
        body: { clientMessageId, message: text },
      }),
      transformResponse: (response: MessageDTO) => toChatMessage(response),
      async onQueryStarted(
        { conversationId, text, clientMessageId },
        { dispatch, queryFulfilled }
      ) {
        const patchMessage = (update: (message: ChatMessage) => void) =>
          dispatch(
            driverChatApi.util.updateQueryData(
              'getDriverChatMessages',
              conversationId,
              (draft) => {
                const message = draft.messages.find(
                  (m) => m.clientMessageId === clientMessageId
                );
                if (message) update(message);
              }
            )
          );

        dispatch(
          driverChatApi.util.updateQueryData(
            'getDriverChatMessages',
            conversationId,
            (draft) => {
              const existing = draft.messages.find(
                (m) => m.clientMessageId === clientMessageId
              );
              if (existing) {
                existing.status = 'sending';
                return;
              }
              draft.messages.push({
                id: clientMessageId,
                clientMessageId,
                conversationId,
                sender: 'DISPATCHER',
                text,
                createdAt: new Date().toISOString(),
                status: 'sending',
              });
            }
          )
        );

        try {
          const { data } = await queryFulfilled;
          patchMessage((message) => {
            Object.assign(message, data, { clientMessageId });
          });
          patchConversations(dispatch, (draft) => {
            const conversation = draft.find(
              (c) => c.conversationId === conversationId
            );
            if (conversation) {
              conversation.lastMessage = {
                sender: data.sender,
                text: data.text,
                createdAt: data.createdAt,
                status: data.status,
              };
            }
          });
        } catch (error) {
          patchMessage((message) => {
            message.status = 'failed';
          });
          // 409 CONVERSATION_NOT_ACTIVE: the ride ended, the thread is read-only now.
          if (
            (error as { error?: { status?: number } })?.error?.status === 409
          ) {
            patchConversations(dispatch, (draft) => {
              const conversation = draft.find(
                (c) => c.conversationId === conversationId
              );
              if (conversation) conversation.conversationStatus = 'COMPLETED';
            });
          }
        }
      },
    }),

    markDriverChatRead: builder.mutation<
      {
        conversationId: number;
        lastReadMessageId: number;
        unreadCount: number;
      },
      ReadArgs
    >({
      query: ({ conversationId, lastReadMessageId }) => ({
        url: `${BASE}/conversations/${conversationId}/read`,
        method: 'POST',
        body: { lastReadMessageId },
      }),
      async onQueryStarted({ conversationId }, { dispatch, queryFulfilled }) {
        const patches = CONVERSATION_FILTERS.map((filter) =>
          dispatch(
            driverChatApi.util.updateQueryData(
              'getDriverConversations',
              { filter },
              (draft) => {
                const conversation = draft.find(
                  (c) => c.conversationId === conversationId
                );
                if (conversation) conversation.unreadCount = 0;
              }
            )
          )
        );
        try {
          await queryFulfilled;
          dispatch(driverChatApi.util.invalidateTags(['Unread']));
        } catch {
          patches.forEach((patch) => patch.undo());
        }
      },
    }),

    getDriverChatUnreadCount: builder.query<number, void>({
      query: () => ({ url: `${BASE}/unread-count` }),
      transformResponse: (response: { count?: number }) => response?.count ?? 0,
      providesTags: ['Unread'],
    }),

    getDriverChatQuickReplies: builder.query<string[], void>({
      query: () => ({ url: `${BASE}/quick-replies` }),
    }),

    // Single-use, 60 s ticket: never cache, fetch a fresh one per connection.
    getDriverChatWsTicket: builder.query<
      { ticket: string; expiresAt: number },
      void
    >({
      // query: () => ({ url: `${BASE}/ws-ticket` }),
      query: () => ({ url: `${BASE_SOCKET}/ws-ticket` }),
      keepUnusedDataFor: 0,
    }),
  }),
});

/** Patches every cached list variant (ACTIVE and TODAY); a missing cache entry is a no-op. */
export function patchConversations(
  dispatch: (action: any) => unknown,
  recipe: (draft: DriverConversation[]) => void
) {
  CONVERSATION_FILTERS.forEach((filter) =>
    dispatch(
      driverChatApi.util.updateQueryData(
        'getDriverConversations',
        { filter },
        recipe
      )
    )
  );
}

export const {
  useGetDriverConversationsQuery,
  useOpenDriverConversationMutation,
  useGetDriverConversationQuery,
  useGetDriverChatMessagesQuery,
  useLoadOlderDriverChatMessagesMutation,
  useSendDriverChatMessageMutation,
  useMarkDriverChatReadMutation,
  useGetDriverChatUnreadCountQuery,
  useGetDriverChatQuickRepliesQuery,
} = driverChatApi;
